import React, {
  useState,
  Fragment,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import { Dialog, Button, Message } from '@alifd/next';
import { Form, Input, Select, Upload, DatePicker } from 'antd';
import client from '@/lib/feathers';
import { uploadFiles, deleteFile } from '@/lib/manipulateFile';
import moment from 'moment';
import { BackStageContext } from '@/contexts/BackStageContext';

const Item = Form.Item;

const inputs = {
  Input,
  Select,
  TextArea: Input.TextArea,
  DatePicker,
  RangePicker: DatePicker.RangePicker,
};

function EditButton({
  dbName = '',
  fields = [],
  func,
  defaultValue = {},
  setIsUpdate = () => {},
  setSelectedVendor = () => {},
}) {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [uploadingList, setUploadingList] = useState([]);

  const { setBackStageUpdateTrigger } = useContext(BackStageContext);

  const formatValue = useMemo(() => {
    const newValue = { ...defaultValue };

    if (defaultValue.contractPeriod) {
      const [start, end] = defaultValue.contractPeriod;
      newValue.contractPeriod = [moment(start), moment(end)];
    }
    if (defaultValue?.companyId) {
      newValue.company = newValue.tableName + '-' + newValue.companyId;
      // setSelectedVendor(newValue.company);
    }
    ['startDate', 'transferDate'].forEach((field) => {
      if (defaultValue[field]) {
        newValue[field] = moment(defaultValue[field]);
      }
    });

    return newValue;
  }, [defaultValue]);

  useEffect(() => {
    form.setFieldsValue(formatValue);
  }, [form, formatValue]);

  const handleSubmit = useCallback(async () => {
    try {
      let values = await form.validateFields();
      values = JSON.parse(JSON.stringify(values));

      ['contractPeriod', 'period'].forEach((field) => {
        if (Object.hasOwnProperty.call(values, field)) {
          values[field] = values[field].map((time) => moment(time).valueOf());
        }
      });
      ['startDate', 'transferDate'].forEach((field) => {
        if (Object.hasOwnProperty.call(values, field)) {
          values[field] = moment(values[field]).valueOf();
        }
      });
      if (values.company) {
        const [tableName, id] = values.company.split('-');
        values.tableName = tableName;
        values.companyId = id;
        delete values.company;
      }
      if (func.deleteStatus) {
        values.activated = true;
      }

      if (defaultValue._id) {
        await client.service(dbName).patch(defaultValue._id, values);
        Message.success('修改成功');
      } else {
        await client.service(dbName).create(values);
        Message.success('新增成功');
      }
      form.resetFields();
      setBackStageUpdateTrigger({});
      setIsUpdate(true);
      setVisible(false);
      setSelectedVendor('');
    } catch (err) {
      console.error({ err });
      Message.error('上傳發生錯誤');
    }
  }, [
    dbName,
    defaultValue._id,
    form,
    func.deleteStatus,
    setBackStageUpdateTrigger,
    setIsUpdate,
    setSelectedVendor,
  ]);
  return (
    <>
      <Button
        onClick={() => {
          setVisible(true);
          if (formatValue?.company) {
            setSelectedVendor(formatValue.company);
          }
        }}
      >
        {func.text}
      </Button>
      <Dialog
        {...{
          visible,
          onCancel: () => {
            if (defaultValue._id) {
              form.setFieldsValue(formatValue);
            } else {
              form.resetFields();
            }
            setVisible(false);
          },
          onClose: () => {
            if (defaultValue._id) {
              form.setFieldsValue(formatValue);
            } else {
              form.resetFields();
            }
            setVisible(false);
          },
          onOk: handleSubmit,
          okProps: {
            loading: uploadingList.length > 0,
            disabled: uploadingList.length > 0,
          },
        }}
        title={func.text}
      >
        <Form
          form={form}
          style={{
            width: '600px',
            maxHeight: '500px',
            overflowY: 'auto',
          }}
          initialValues={formatValue}
          onFieldsChange={(changedFields) => {
            if (changedFields[0].name.includes('company')) {
              form.setFieldsValue({
                ...form.getFieldsValue(),
                service: [],
              });
            }
          }}
        >
          {Object.values(fields)
            .filter((field) => field.input.type !== 'hidden')
            .map((field, index) => {
              let fieldComponent = <></>;
              if (field.input.type === 'Upload') {
                fieldComponent = (
                  <Item
                    {...field.item.props}
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      const { file, fileList } = e;
                      if (file.status === 'uploading') {
                        setUploadingList((prev) => [
                          ...prev,
                          {
                            uid: file.uid,
                            name: file.name,
                          },
                        ]);
                      } else {
                        setUploadingList((prev) =>
                          prev.filter((item) => item.uid !== file.uid)
                        );
                      }
                      return fileList;
                    }}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value) {
                            if (
                              value
                                .map((item) => item.status)
                                .includes('uploading')
                            ) {
                              return Promise.reject(new Error('上傳中'));
                            }
                            if (
                              value.map((item) => item.status).includes('error')
                            ) {
                              return Promise.reject(
                                new Error('上傳失敗，請重新上傳或者移除檔案')
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Upload
                      {...field.input.props}
                      fileList={form.getFieldValue(field.name)}
                      customRequest={async (options) => {
                        const { onSuccess, onError, file } = options;
                        if (file.size > 1024 * 1024 * 20) {
                          onError({ message: '上傳檔案不能超過20MB' });
                          return;
                        }
                        try {
                          const res = await uploadFiles(dbName, options.file);
                          onSuccess(res.data);
                        } catch (err) {
                          onError(err, null);
                          console.error({ err });
                        }
                      }}
                      onRemove={async (file) => {
                        try {
                          await deleteFile(file);
                        } catch (err) {
                          console.error({ err });
                        }
                      }}
                    >
                      <Button>上傳</Button>
                    </Upload>
                  </Item>
                );
                return <Fragment key={index}>{fieldComponent}</Fragment>;
              } else {
                // if (field.item.props.name !== 'service') {
                if (dbName !== 'menuWorkers') {
                  const InputComponent = inputs[field.input.type];
                  fieldComponent = (
                    <Item {...field.item.props}>
                      <InputComponent {...field.input.props} />
                    </Item>
                  );

                  return <Fragment key={index}>{fieldComponent}</Fragment>;
                } else {
                  const InputComponent = inputs[field.input.type];

                  fieldComponent = (
                    <Item {...field.item.props}>
                      <InputComponent {...field.input.props} />
                    </Item>
                  );
                  if (field.item.props.name !== 'service') {
                    return <Fragment key={index}>{fieldComponent}</Fragment>;
                  }
                  if (
                    (field.item.props.name === 'service' &&
                      form.getFieldValue('company')) ||
                    formatValue?.company
                  ) {
                    return <Fragment key={index}>{fieldComponent}</Fragment>;
                  }
                }
              }
            })}
        </Form>
      </Dialog>
    </>
  );
}

export default React.memo(EditButton);
